import React, { useState } from 'react';
import styled, { useTheme } from 'styled-components';
import { useTranslation } from 'react-i18next';
import { useParams, useHistory } from 'react-router-dom';
import {
  Download, ArrowLeft, CheckCircle2, XCircle, ArrowUp, ArrowDown, Minus,
} from 'lucide-react';

import Button from '../../components/ui/Button';
import { useAuth } from '../../context/AuthContext';
import { formatDate, formatDateTime } from '../../utils/Utils';
import useReportCardData from './config';
import { buildReportCardPdfLabels, downloadReportCardPdf } from './config/reportCardPdf';

// P1-C — performans karnesi (PDF sunucuda üretilir — QuestPDF; bu sayfa
// ekran görünümüdür, "PDF Olarak İndir" sunucu uçlarını çağırır). İki varyant:
//   /performance/report-card/:periodId              → SELF (KVKK: 360
//     yorumları/OverallLevel ASLA — veri kaynağı my/period-results, bu
//     alanlar tip düzeyinde zaten yok. FinalLevel — Plan 5 Task 2'den beri
//     KOŞULLU: BE'nin tebliğ koşulu sağlandığında `finalLevelLabel` alanı
//     JSON'da gelir, aksi halde hiç gelmez; FE koşulu HESAPLAMAZ, DTO-driven
//     render — bkz. aşağıdaki `!isManagerVariant && finalLevelLabel` bloğu)
//   /performance/report-card/:periodId/:employeeId  → yönetici/İK (PM.Read +
//     BE kapsam kontrolü; nitel alanlar + FinalLevel dahil, FARKLI bir DTO'dan
//     — feedback-responses/summary/history; bu blok DEĞİŞMEDİ)
// Tema: renkler yalnız theme token'larından (DistributionChartCard deseni —
// hardcode YASAK).
const Container = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const Toolbar = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  max-width: 820px;
  margin-bottom: 14px;
  gap: 8px;
`;

const Sheet = styled.div`
  width: 100%;
  max-width: 820px;
  background: #fff;
  border: 1px solid #e8ecf0;
  border-radius: 10px;
  padding: 32px 36px;
`;

const SheetHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 12px;
  border-bottom: 2px solid ${({ theme }) => theme.colors.primary.main};
  padding-bottom: 14px;
  margin-bottom: 18px;
`;

const SheetTitle = styled.h1`
  margin: 0;
  font-size: 20px;
  font-weight: ${({ theme }) => theme.metrics.font.weight.bold};
  color: ${({ theme }) => theme.colors.text.primary};
`;

const SheetSubtitle = styled.div`
  font-size: ${({ theme }) => theme.metrics.font.size.sm};
  color: ${({ theme }) => theme.colors.text.dark.little};
  margin-top: 4px;
`;

const IdentityBlock = styled.div`
  text-align: right;
`;

const IdentityName = styled.div`
  font-size: ${({ theme }) => theme.metrics.font.size.md};
  font-weight: ${({ theme }) => theme.metrics.font.weight.semiBold};
  color: ${({ theme }) => theme.colors.text.primary};
`;

const IdentityMeta = styled.div`
  font-size: ${({ theme }) => theme.metrics.font.size.xs};
  color: ${({ theme }) => theme.colors.text.dark.little};
  margin-top: 2px;
`;

const Section = styled.section`
  margin-bottom: 22px;
  break-inside: avoid;
`;

const SectionTitle = styled.h2`
  margin: 0 0 10px;
  font-size: ${({ theme }) => theme.metrics.font.size.md};
  font-weight: ${({ theme }) => theme.metrics.font.weight.bold};
  color: ${({ theme }) => theme.colors.text.primary};
  border-bottom: 1px solid #e8ecf0;
  padding-bottom: 6px;
`;

const StyledTable = styled.table`
  width: 100%;
  border-collapse: collapse;

  th, td {
    text-align: left;
    padding: 6px 8px;
    font-size: ${({ theme }) => theme.metrics.font.size.sm};
    border-bottom: 1px solid #f1f5f9;
    vertical-align: top;
  }

  th {
    color: ${({ theme }) => theme.colors.text.dark.little};
    font-weight: ${({ theme }) => theme.metrics.font.weight.semiBold};
  }

  td {
    color: ${({ theme }) => theme.colors.text.primary};
  }
`;

const GoalBlock = styled.div`
  margin-bottom: 14px;
  break-inside: avoid;
`;

const GoalHeadRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 6px;
`;

const TypeBadge = styled.span`
  display: inline-block;
  padding: 2px 8px;
  border-radius: 10px;
  font-size: ${({ theme }) => theme.metrics.font.size.xs};
  font-weight: ${({ theme }) => theme.metrics.font.weight.semiBold};
  background: ${({ theme }) => theme.colors.bgColor};
  color: ${({ theme }) => theme.colors.primary.main};
`;

const GoalTitle = styled.span`
  font-size: ${({ theme }) => theme.metrics.font.size.sm};
  font-weight: ${({ theme }) => theme.metrics.font.weight.semiBold};
  color: ${({ theme }) => theme.colors.text.primary};
`;

const ScoreRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 10px 14px;
  border: 1px solid #e8ecf0;
  border-radius: 8px;
  margin-bottom: 12px;
`;

const ScoreLabel = styled.div`
  font-size: ${({ theme }) => theme.metrics.font.size.sm};
  color: ${({ theme }) => theme.colors.text.primary};
  font-weight: ${({ theme }) => theme.metrics.font.weight.semiBold};
`;

const ScoreValue = styled.div`
  font-size: 22px;
  font-weight: ${({ theme }) => theme.metrics.font.weight.bold};
  color: ${({ theme }) => theme.colors.primary.main};
`;

const BarOuter = styled.div`
  width: 120px;
  height: 8px;
  background: #f1f5f9;
  border-radius: 4px;
  overflow: hidden;
  display: inline-block;
  vertical-align: middle;
  -webkit-print-color-adjust: exact;
  print-color-adjust: exact;
`;

const BarInner = styled.div`
  height: 100%;
  border-radius: 4px;
  background: ${({ theme }) => theme.colors.primary.main};
  width: ${(p) => p.$pct}%;
`;

const CommentList = styled.ul`
  margin: 4px 0 0;
  padding-left: 18px;

  li {
    font-size: ${({ theme }) => theme.metrics.font.size.xs};
    color: ${({ theme }) => theme.colors.text.dark.little};
    margin-bottom: 2px;
    white-space: pre-wrap;
  }
`;

const LevelBadge = styled.span`
  display: inline-block;
  padding: 3px 10px;
  border-radius: 12px;
  font-size: ${({ theme }) => theme.metrics.font.size.xs};
  font-weight: ${({ theme }) => theme.metrics.font.weight.semiBold};
  background: ${({ theme }) => theme.colors.bgColor};
  color: ${({ theme }) => theme.colors.text.primary};
`;

// Faz E (spec 2026-08-08 §4, E5) — "Yetkinlik Kırılımı" bölümü, HER İKİ
// varyant (self/manager) da AYNI CompetencyBreakdownSection'ı kullanır;
// hedef-kıyas dalı `item.targetLevel != null` koşuluyla DTO-driven açılır
// (ManagerCompetencyBreakdownDto'da var, EmployeeCompetencyBreakdownDto'da
// TİP DÜZEYİNDE yok — task-3-report.md §1, alan yoksa bu bölüm hiç render
// edilmez, FE bir isManagerVariant koşulu İCAT ETMEZ).
const CompetencyRow = styled.div`
  margin-bottom: 16px;
  break-inside: avoid;
  &:last-child { margin-bottom: 0; }
`;

const CompetencyRowHeader = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 10px;
  margin-bottom: 6px;
`;

const CompetencyName = styled.span`
  font-size: ${({ theme }) => theme.metrics.font.size.sm};
  font-weight: ${({ theme }) => theme.metrics.font.weight.semiBold};
  color: ${({ theme }) => theme.colors.text.primary};
`;

const CompetencyQuestionCount = styled.span`
  font-size: ${({ theme }) => theme.metrics.font.size.xs};
  color: ${({ theme }) => theme.colors.text.dark.little};
  white-space: nowrap;
`;

const CompetencyAverageInline = styled.span`
  font-size: ${({ theme }) => theme.metrics.font.size.xs};
  color: ${({ theme }) => theme.colors.text.dark.little};
  margin-left: 6px;
`;

const TargetRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 4px;
`;

const TargetText = styled.span`
  font-size: ${({ theme }) => theme.metrics.font.size.xs};
  color: ${({ theme }) => theme.colors.text.dark.little};
`;

// #1B7A3E/#B02A37 — FeedbackFormsTab.js'in (Task 4) WeightsTotal dengeli/
// dengesiz tonuyla AYNI hardcoded çift (komşu idiom); "at" (hedefte) nötr
// text.dark.little.
const TargetBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 3px;
  font-size: ${({ theme }) => theme.metrics.font.size.xs};
  font-weight: 700;
  color: ${({ theme, $tone }) => (
    $tone === 'above' ? '#1B7A3E' : $tone === 'below' ? '#B02A37' : theme.colors.text.dark.little
  )};
`;

const AckRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: ${({ theme }) => theme.metrics.font.size.sm};
  color: ${({ theme }) => theme.colors.text.primary};
`;

const EmptyNote = styled.div`
  font-size: ${({ theme }) => theme.metrics.font.size.sm};
  color: ${({ theme }) => theme.colors.text.dark.little};
  padding: 8px 0;
`;

const CenteredCard = styled.div`
  width: 100%;
  max-width: 820px;
  background: #fff;
  border: 1px solid #e8ecf0;
  border-radius: 10px;
  padding: 48px 24px;
  text-align: center;
  font-size: ${({ theme }) => theme.metrics.font.size.sm};
  color: ${({ theme }) => theme.colors.text.dark.little};
`;

function clampPct(v) {
  const n = Number(v);
  if (Number.isNaN(n)) return 0;
  return Math.min(100, Math.max(0, n));
}

function GoalsSection({ goals, t }) {
  return (
    <Section>
      <SectionTitle>{t('performance.reportCard.goalsSection')}</SectionTitle>
      {(!goals || goals.length === 0) ? (
        <EmptyNote>{t('performance.reportCard.noGoals')}</EmptyNote>
      ) : goals.map((goal) => (
        <GoalBlock key={goal.id}>
          <GoalHeadRow>
            <TypeBadge>{t(`performance.goal.goalType.${goal.goalType}`, { defaultValue: goal.goalType })}</TypeBadge>
            <GoalTitle>{goal.title}</GoalTitle>
          </GoalHeadRow>
          {goal.goalType === 'okr' ? (
            <StyledTable>
              <thead>
                <tr>
                  <th>{t('performance.goal.keyResultTitle')}</th>
                  <th>{t('performance.goal.employeeScore')}</th>
                  <th>{t('performance.goal.managerScore')}</th>
                </tr>
              </thead>
              <tbody>
                {(goal.keyResults || []).map((kr) => (
                  <tr key={kr.id}>
                    <td>{kr.title}</td>
                    <td>{kr.employeeScore ?? '-'}</td>
                    <td>{kr.managerScore ?? '-'}</td>
                  </tr>
                ))}
              </tbody>
            </StyledTable>
          ) : (
            <StyledTable>
              <thead>
                <tr>
                  <th>{t('performance.goal.kpiIndicatorName')}</th>
                  <th>{t('performance.goal.kpiTargetValue')}</th>
                  <th>{t('performance.goal.kpiActualValue')}</th>
                  <th>{t('performance.goal.kpiCompletionPct')}</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>{goal.kpiIndicatorName || '-'}</td>
                  <td>{goal.kpiTargetValue ?? '-'}</td>
                  <td>{goal.kpiActualValue ?? '-'}</td>
                  <td>
                    <BarOuter><BarInner $pct={clampPct(goal.completionPct)} /></BarOuter>
                    {' '}
                    {goal.completionPct != null ? `${goal.completionPct}%` : '-'}
                  </td>
                </tr>
              </tbody>
            </StyledTable>
          )}
        </GoalBlock>
      ))}
    </Section>
  );
}

// isManagerVariant: yorumlar (open_text Comments) yalnız yönetici/İK karnesinde
// — self varyantın veri kaynağında bu alanlar tip düzeyinde zaten yok, bu bayrak
// ikinci (görsel) savunma katmanı.
function FeedbackSection({ feedback, isManagerVariant, t }) {
  const perQuestion = feedback?.perQuestion || [];

  return (
    <Section>
      <SectionTitle>{t('performance.reportCard.feedbackSection')}</SectionTitle>
      {!feedback ? (
        <EmptyNote>{t('performance.reportCard.noFeedback')}</EmptyNote>
      ) : (
        <>
          <ScoreRow>
            <ScoreLabel>{t('performance.results.weightedOverall')}</ScoreLabel>
            <ScoreValue>{feedback.weightedOverall != null ? feedback.weightedOverall : '-'}</ScoreValue>
          </ScoreRow>
          {perQuestion.length > 0 && (
            <StyledTable>
              <thead>
                <tr>
                  <th>{t('performance.results.question')}</th>
                  <th>{t('performance.results.average')}</th>
                </tr>
              </thead>
              <tbody>
                {perQuestion.map((q) => (
                  <tr key={q.questionId}>
                    <td>
                      {q.text}
                      {isManagerVariant && q.comments && q.comments.length > 0 && (
                        <CommentList>
                          {q.comments.map((c, idx) => <li key={idx}>{c}</li>)}
                        </CommentList>
                      )}
                    </td>
                    <td>
                      {q.average != null ? (
                        <>
                          <BarOuter><BarInner $pct={clampPct((Number(q.average) / 5) * 100)} /></BarOuter>
                          {' '}
                          {q.average}
                        </>
                      ) : '-'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </StyledTable>
          )}
        </>
      )}
    </Section>
  );
}

// Faz E (spec 2026-08-08 §4, E5) — "Yetkinlik Kırılımı": HER İKİ varyantta da
// aynı fonksiyon (task-3-report.md §1: Manager/Employee DTO'ları arasındaki
// TEK fark `targetLevel` alanının varlığı) — self varyantta `item.targetLevel`
// hiç gelmediği için hedef-kıyas bloğu kendiliğinden render edilmez. Boş
// kırılım (etiketli soru yok) → bölüm hiç görünmez.
function CompetencyBreakdownSection({ breakdown, t }) {
  if (!breakdown || breakdown.length === 0) return null;

  return (
    <Section data-role="competency-breakdown-section">
      <SectionTitle>{t('competencyBreakdown.cardTitle')}</SectionTitle>
      {breakdown.map((item) => {
        const hasAverage = item.average != null;
        const hasTarget = item.targetLevel != null;
        const diff = hasAverage && hasTarget ? Number(item.average) - item.targetLevel : null;

        return (
          <CompetencyRow key={item.competencyId} data-role="competency-breakdown-row">
            <CompetencyRowHeader>
              <CompetencyName>{item.name}</CompetencyName>
              <CompetencyQuestionCount>
                {t('competencyBreakdown.questionCount', { count: item.questionCount })}
              </CompetencyQuestionCount>
            </CompetencyRowHeader>
            {hasAverage ? (
              <>
                <BarOuter><BarInner $pct={clampPct((Number(item.average) / 5) * 100)} /></BarOuter>
                <CompetencyAverageInline>{item.average} / 5</CompetencyAverageInline>
              </>
            ) : (
              <CompetencyAverageInline>-</CompetencyAverageInline>
            )}
            {hasTarget && (
              <TargetRow>
                <TargetText>{t('competencyBreakdown.targetLabel')}: {item.targetLevel}</TargetText>
                {diff != null && (
                  diff > 0 ? (
                    <TargetBadge $tone="above"><ArrowUp size={12} />{t('competencyBreakdown.aboveTarget')}</TargetBadge>
                  ) : diff < 0 ? (
                    <TargetBadge $tone="below"><ArrowDown size={12} />{t('competencyBreakdown.belowTarget')}</TargetBadge>
                  ) : (
                    <TargetBadge $tone="at"><Minus size={12} />{t('competencyBreakdown.atTarget')}</TargetBadge>
                  )
                )}
              </TargetRow>
            )}
          </CompetencyRow>
        );
      })}
    </Section>
  );
}

export default function PerformanceReportCardPage() {
  const { t, i18n } = useTranslation();
  const theme = useTheme();
  const historyRouter = useHistory();
  const { periodId, employeeId } = useParams();
  const { user } = useAuth();
  const isManagerVariant = !!employeeId;

  const {
    period, employee, goals, feedback, managerExtras, acknowledgement, finalLevelLabel, notReady, fetching,
  } = useReportCardData(periodId, employeeId);

  const lang = i18n.language;
  const [downloading, setDownloading] = useState(false);

  const displayName = isManagerVariant
    ? (employee ? `${employee.firstName} ${employee.lastName}` : '...')
    : (user?.fullName || `${user?.firstName || ''} ${user?.lastName || ''}`.trim());

  const handleDownloadPdf = async () => {
    if (downloading) return;
    setDownloading(true);
    try {
      await downloadReportCardPdf({
        periodId,
        employeeId,
        lang,
        labels: buildReportCardPdfLabels(t),
        // Yalnız Content-Disposition okunamazsa dosya adına girer (yüklenmemiş çalışan "..." ise adsız ad).
        displayName: displayName === '...' ? '' : displayName,
      });
    } finally {
      setDownloading(false);
    }
  };

  const acknowledgedAt = isManagerVariant ? managerExtras?.acknowledgedAt : acknowledgement?.acknowledgedAt;

  if (fetching) {
    return <Container><CenteredCard>{t('common.loading')}</CenteredCard></Container>;
  }

  if (!isManagerVariant && notReady) {
    return (
      <Container>
        <Toolbar>
          <Button variant="ghost" onClick={() => historyRouter.goBack()}>
            <ArrowLeft size={15} style={{ marginRight: 6, verticalAlign: 'text-bottom' }} />
            {t('common.back')}
          </Button>
        </Toolbar>
        <CenteredCard>{t('responseErrors.performance.results.notReady')}</CenteredCard>
      </Container>
    );
  }

  return (
    <Container>
      <Toolbar>
        <Button variant="ghost" onClick={() => historyRouter.goBack()}>
          <ArrowLeft size={15} style={{ marginRight: 6, verticalAlign: 'text-bottom' }} />
          {t('common.back')}
        </Button>
        <Button onClick={handleDownloadPdf} disabled={downloading}>
          <Download size={15} style={{ marginRight: 6, verticalAlign: 'text-bottom' }} />
          {downloading ? t('performance.reportCard.downloading') : t('performance.reportCard.downloadPdf')}
        </Button>
      </Toolbar>

      <Sheet id="report-card-sheet">
        <SheetHeader>
          <div>
            <SheetTitle>{t('performance.reportCard.title')}</SheetTitle>
            <SheetSubtitle>
              {period?.name || '-'}
              {period ? ` · ${formatDate(period.startDate, lang)} — ${formatDate(period.endDate, lang)}` : ''}
            </SheetSubtitle>
          </div>
          <IdentityBlock>
            <IdentityName>{displayName}</IdentityName>
            <IdentityMeta>{t('performance.reportCard.printedAt')}: {formatDate(new Date(), lang)}</IdentityMeta>
          </IdentityBlock>
        </SheetHeader>

        {/* Nihai seviye — yönetici/İK varyantı (mevcut davranış DEĞİŞMEDİ; farklı
            bir DTO'dan okunur — feedback-responses/summary/history). */}
        {isManagerVariant && managerExtras?.finalLevelLabel && (
          <Section>
            <SectionTitle>{t('performance.reportCard.finalLevelSection')}</SectionTitle>
            <LevelBadge>{managerExtras.finalLevelLabel}</LevelBadge>
          </Section>
        )}

        {/* Nihai seviye — çalışan (SELF) varyantı, Plan 5 Task 2. DTO-driven:
            `finalLevelLabel` my/period-results ucundan yalnız BE'nin tebliğ
            koşulu sağlandığında gelir (bkz. Task 1 raporu); FE koşul
            HESAPLAMAZ, alan yoksa bu satır hiç render edilmez. */}
        {!isManagerVariant && finalLevelLabel && (
          <Section data-role="final-level-row">
            <SectionTitle>{t('performance.reportCard.finalLevelSection')}</SectionTitle>
            <LevelBadge>{finalLevelLabel}</LevelBadge>
          </Section>
        )}

        {(period?.systemAEnabled ?? true) && <GoalsSection goals={goals} t={t} />}
        {(period?.systemBEnabled ?? true) && (
          <FeedbackSection feedback={feedback} isManagerVariant={isManagerVariant} t={t} />
        )}
        {/* Faz E (spec §4, E5) — "Yetkinlik Kırılımı"; self varyantta
            targetLevel DTO'da hiç yok, CompetencyBreakdownSection kendi
            içinde DTO-driven karar verir (bkz. fonksiyon yorumu). */}
        {(period?.systemBEnabled ?? true) && (
          <CompetencyBreakdownSection breakdown={feedback?.competencyBreakdown} t={t} />
        )}

        {/* Yönetici genel değerlendirmesi (OverallLevel) — yalnız yönetici/İK varyantı. */}
        {isManagerVariant && feedback?.overallLevelLabel && (
          <Section>
            <SectionTitle>{t('performance.reportCard.overallLevelSection')}</SectionTitle>
            <LevelBadge>{feedback.overallLevelLabel}</LevelBadge>
          </Section>
        )}

        <Section>
          <SectionTitle>{t('performance.reportCard.ackSection')}</SectionTitle>
          {acknowledgedAt ? (
            <AckRow>
              <CheckCircle2 size={16} color={theme.colors.secondary.main} />
              {t('performance.reportCard.acknowledged')} — {formatDateTime(acknowledgedAt, lang)}
              {!isManagerVariant && acknowledgement?.counterNote
                ? ` (${t('performance.results.counterNote')}: ${acknowledgement.counterNote})`
                : ''}
            </AckRow>
          ) : (
            <AckRow>
              <XCircle size={16} color={theme.colors.text.muted} />
              {t('performance.reportCard.notAcknowledged')}
            </AckRow>
          )}
        </Section>
      </Sheet>
    </Container>
  );
}
